import { memo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  FaExchangeAlt,
  FaChartLine,
  FaTemperatureHigh,
  FaLeaf,
} from "react-icons/fa";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const BenefitCard = memo(({ benefit }) => (
  <motion.div
    variants={itemVariants}
    whileHover={{ y: -10, transition: { duration: 0.3 } }}
    className="bg-gradient-to-b from-gray-800 to-gray-900 p-6 rounded-xl shadow-lg"
  >
    <div className="inline-flex justify-center items-center w-14 h-14 rounded-full bg-blue-900/30 mb-4">
      {benefit.icon}
    </div>
    <h3 className="text-lg font-bold mb-2">{benefit.name}</h3>
    <p className="text-gray-400">{benefit.description}</p>
  </motion.div>
));

BenefitCard.displayName = "BenefitCard";

BenefitCard.propTypes = {
  benefit: PropTypes.shape({
    icon: PropTypes.node.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
};

const PVTBenefits = ({ detailData }) => {
  const { t } = useTranslation();
  const pvtBenefitsData = t("components.pvt.pvtBenefits", {
    returnObjects: true,
  });

  const benefits = detailData
    ? [
        {
          name: detailData.benefits.dualEnergy.name,
          description: detailData.benefits.dualEnergy.description,
          icon: <FaExchangeAlt className="text-yellow-400" />,
        },
        {
          name: detailData.benefits.higherEfficiency.name,
          description: detailData.benefits.higherEfficiency.description,
          icon: <FaChartLine className="text-green-400" />,
        },
        {
          name: detailData.benefits.thermalBoost.name,
          description: detailData.benefits.thermalBoost.description,
          icon: <FaTemperatureHigh className="text-red-400" />,
        },
        {
          name: detailData.benefits.ecoFriendly.name,
          description: detailData.benefits.ecoFriendly.description,
          icon: <FaLeaf className="text-green-400" />,
        },
      ]
    : [];

  if (!benefits || benefits.length === 0) return null;

  return (
    <motion.div
      className="mb-16"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "0px 0px -100px 0px" }}
      id="safePad"
    >
      <h2 className="text-xl md:text-2xl font-bold mb-8 text-center">
        {pvtBenefitsData.title}
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {benefits.map((benefit, index) => (
          <BenefitCard key={index} benefit={benefit} />
        ))}
      </div>
    </motion.div>
  );
};

export default memo(PVTBenefits);

PVTBenefits.propTypes = {
  detailData: PropTypes.shape({
    benefits: PropTypes.shape({
      dualEnergy: PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      }).isRequired,
      higherEfficiency: PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      }).isRequired,
      thermalBoost: PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      }).isRequired,
      ecoFriendly: PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};
